import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import type { UseOverviewAlertsResult } from '@hooks/dashboard/useOverviewAlerts'
import type { Notification } from '@dataTypes/notification.types'
import { navigateToNotificationTarget } from '@utils/notification.util'
import {
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'

type OverviewAlertsCardProps = {
  className?: string
  alerts: UseOverviewAlertsResult
}

type AlertGroupProps = {
  label: string
  count: number
  items: Notification[]
  onAction: (notification: Notification) => void
}

function AlertGroup({
  label,
  count,
  items,
  onAction,
}: AlertGroupProps): React.ReactElement {
  return (
    <details>
      <summary>
        <span className="alerts-row-label">{label}</span>
        <span className="alerts-row-count-pill">
          <span className="alerts-row-count">{count}</span>
        </span>
      </summary>
      <div className="alerts-row-content">
        {items.length === 0 ? (
          <div className="overview-alerts-empty">No unread alerts</div>
        ) : (
          <ul className="overview-alerts-item-list">
            {items.map((item) => {
              const id = item.notification_id || item.id
              const showAction = Boolean(
                item.actionButtonText &&
                  (item.action_url || item.action_metadata),
              )
              return (
                <li key={id} className="overview-alerts-item">
                  <div className="overview-alerts-item-main">
                    <span className="overview-alerts-item-title">
                      {item.title}
                    </span>
                    {(item.date || item.time) && (
                      <span className="overview-alerts-item-meta">
                        {[item.date, item.time].filter(Boolean).join(' • ')}
                      </span>
                    )}
                    {item.message ? (
                      <span className="overview-alerts-item-message">
                        {item.message}
                      </span>
                    ) : null}
                  </div>
                  {showAction ? (
                    <button
                      type="button"
                      className="overview-alerts-item-action"
                      onClick={() => onAction(item)}
                    >
                      {item.actionButtonText}
                    </button>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </details>
  )
}

export function OverviewAlertsCard({
  className,
  alerts,
}: OverviewAlertsCardProps): React.ReactElement {
  const navigate = useNavigate()
  const {
    critical,
    urgent,
    criticalCount,
    urgentCount,
    loading,
    error,
    markRead,
  } = alerts

  const handleAction = useCallback(
    (notification: Notification): void => {
      const navigated = navigateToNotificationTarget(navigate, notification)
      const id = notification.notification_id || notification.id
      if (navigated && id) {
        void markRead(id)
      }
    },
    [markRead, navigate],
  )

  const alertsHasPayload =
    Boolean(error) || criticalCount > 0 || urgentCount > 0
  const isEmpty = shouldShowDashboardSectionEmpty(loading, alertsHasPayload)
  const pending = isDashboardSectionPending(loading, alertsHasPayload)

  return (
    <DashboardSectionCard
      title="Alerts"
      className={className}
      loading={pending}
      skeleton={{ block: 'tall' }}
      isEmpty={isEmpty}
    >
      {error ? (
        <div className="overview-alerts-status overview-alerts-status--error">
          {error}
        </div>
      ) : (
        <div className="dashboard-alerts-list">
          {criticalCount === 0 && urgentCount === 0 ? (
            <div className="overview-alerts-status">
              No unread critical/urgent alerts
            </div>
          ) : null}
          <AlertGroup
            label="Critical"
            count={criticalCount}
            items={critical}
            onAction={handleAction}
          />
          <AlertGroup
            label="Urgent"
            count={urgentCount}
            items={urgent}
            onAction={handleAction}
          />
        </div>
      )}
    </DashboardSectionCard>
  )
}
